import type { ReactNode } from 'react';
import { MultiSelect } from '~/components/multi-select';
import type { LookupOption } from '~/lib/lookups/mock';

interface EntityMultiSelectProps {
	value: number[];
	onChange: (ids: number[]) => void;
	options: LookupOption[];
	placeholder?: string;
	loadOptions?: (query: string) => Promise<LookupOption[]>;
	disabled?: boolean;
	renderChip?: (item: LookupOption) => ReactNode;
}

/**
 * Adapts the object-based MultiSelect to id-array form fields (the policy token
 * stores ProductIds/CategoryIds/… as number[]). Ids missing from `options`
 * (e.g. async-loaded selections not yet cached) fall back to a `#id` label —
 * proper selected-object caching is refined in Phase I.
 */
export function EntityMultiSelect({ value, onChange, options, placeholder, loadOptions, disabled, renderChip }: EntityMultiSelectProps) {
	const selected = value.map(id => options.find(o => o.id === id) ?? { id, label: `#${id}` });
	return (
		<MultiSelect<LookupOption>
			value={selected}
			onChange={next => onChange(next.map(o => o.id))}
			options={options}
			loadOptions={loadOptions}
			getKey={o => String(o.id)}
			getLabel={o => o.label}
			placeholder={placeholder}
			disabled={disabled}
			renderChip={renderChip}
			renderOption={o =>
				o.sublabel ? (
					<span>
						<span className="font-mono text-foreground-subtle">{o.sublabel}</span> · {o.label}
					</span>
				) : (
					<span>{o.label}</span>
				)
			}
		/>
	);
}
